/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import isDestroyed from 'cio/utils/is-destroyed';

interface StickyContainerArgs {
  top?: number;
  bottom?: number;
}

enum Role {
  Container = 'container',
  Trigger = 'trigger',
  Child = 'child',
}

export default class StickyContainer extends Component<StickyContainerArgs> {
  container: HTMLDivElement | null = null;
  trigger: HTMLDivElement | null = null;
  options: IntersectionObserverInit = {
    root: null,
    threshold: [0, 1.0],
  };
  intersectionObserver: IntersectionObserver | null = null;
  resizeObserver: ResizeObserver | null = null;

  @tracked child: HTMLDivElement | null = null;
  @tracked isSticky = false;
  @tracked width = 0;

  willDestroy() {
    super.willDestroy();

    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  get anchorPosition(): keyof StickyContainerArgs {
    return this.args.bottom != null ? 'bottom' : 'top';
  }

  get anchorOffset() {
    return this.args[this.anchorPosition] ?? 0;
  }

  get childStyles() {
    if (this.child != null && this.isSticky) {
      return htmlSafe(`position:fixed; ${this.anchorPosition}:${this.anchorOffset}px; width:${this.width}px;`);
    }

    return '';
  }

  // Generic for storing the three elements. After insert, attempt to setup the
  // observer. If all required elements are set, it will return early.
  @action
  handleInsert(role: Role, el: HTMLDivElement) {
    this[role] = el;
    this.setupObserver();
  }

  @action
  setupObserver() {
    // Return early if all elements haven't been set yet.
    // Gracefully degrade for browsers that don't support IntersectionObserver
    // or ResizeObserver
    if (
      this.container === null ||
      this.trigger === null ||
      this.child === null ||
      window.IntersectionObserver == null ||
      window.ResizeObserver == null
    ) {
      return;
    }

    this.intersectionObserver?.disconnect();
    this.options.rootMargin = `-${this.anchorOffset}px`;
    this.intersectionObserver = new IntersectionObserver(this.handleIntersection, this.options);
    this.resizeObserver = new ResizeObserver(this.handleResize);

    this.intersectionObserver.observe(this.trigger);
    this.resizeObserver.observe(this.container);
  }

  // Handle changes in observation/position. Since only one element is being
  // obsserved, there will only be one entry.
  @action
  handleIntersection([entry]: IntersectionObserverEntry[]) {
    this.isSticky = entry.intersectionRatio < 1;
    this.width = this.container?.offsetWidth ?? 0;
  }

  // Handle changes in size. Since only one element is being
  // obsserved, there will only be one entry.
  @action
  handleResize([entry]: ResizeObserverEntry[]) {
    if (isDestroyed(this)) {
      return;
    }

    this.width = this.container?.offsetWidth ?? 0;
    if (entry.contentBoxSize) {
      // Firefox implements `contentBoxSize` as a single content rect, rather than an array
      let contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;

      this.width = contentBoxSize.inlineSize;
    } else {
      this.width = entry.contentRect.width;
    }
  }
}
